import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle,
  Star,
  Trophy,
  Target,
  DollarSign,
  Lightbulb,
  AlertCircle,
  CheckSquare,
  Home,
  Car,
  Zap,
  Heart,
  BookOpen,
  Film,
  ShoppingBag,
  Package,
  X,
} from "lucide-react";
import { getCurrentUser } from "../utils/auth";
import Layout from "../components/Layout";
import { Challenge } from "../types";

const ChallengePage: React.FC = () => {
  useParams(); // Para manter a funcionalidade de roteamento
  const [user] = useState(getCurrentUser());
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(0);
  const [budget] = useState(2000);
  const [expenses, setExpenses] = useState<{ [key: string]: number }>({});
  const [savings, setSavings] = useState(0);
  const [challengeCompleted, setChallengeCompleted] = useState(false);
  const [showHints, setShowHints] = useState(false);
  const [score, setScore] = useState(0);

  // Dados do desafio (simulados)
  const [challenge] = useState<Challenge>({
    id: "budget-challenge",
    title: "Create Your First Budget",
    description:
      "Learn to organize your finances by creating a realistic monthly budget",
    scenario:
      "You receive $2,000 per month and need to organize your finances. Your goal is to save at least 20% of your income.",
    budget: 2000,
    goals: [
      "Save at least 20% of income ($400)",
      "Cover all essential expenses",
      "Maintain an emergency fund",
      "Avoid unnecessary expenses",
    ],
    successCriteria: [
      "Savings >= $400",
      "Essential expenses covered",
      "Balanced budget",
      "No deficit",
    ],
    hints: [
      "Start by listing your fixed expenses (rent, bills, etc.)",
      "Set aside 20% for savings before spending on other things",
      "Use the 50-30-20 rule: 50% needs, 30% wants, 20% savings",
      "Review your budget monthly and adjust as needed",
    ],
  });

  const expenseCategories = [
    { id: "housing", name: "Housing", icon: Home, essential: true, max: 1000 },
    {
      id: "food",
      name: "Food",
      icon: DollarSign,
      essential: true,
      max: 400,
    },
    {
      id: "transport",
      name: "Transporte",
      icon: Car,
      essential: true,
      max: 300,
    },
    {
      id: "utilities",
      name: "Bills (electricity, water, etc.)",
      icon: Zap,
      essential: true,
      max: 200,
    },
    { id: "health", name: "Health", icon: Heart, essential: true, max: 150 },
    {
      id: "education",
      name: "Education",
      icon: BookOpen,
      essential: false,
      max: 200,
    },
    {
      id: "entertainment",
      name: "Entretenimento",
      icon: Film,
      essential: false,
      max: 150,
    },
    {
      id: "shopping",
      name: "Compras",
      icon: ShoppingBag,
      essential: false,
      max: 100,
    },
    { id: "other", name: "Outros", icon: Package, essential: false, max: 100 },
  ];

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
  }, [user, navigate]);

  const totalExpenses = Object.values(expenses).reduce(
    (sum, val) => sum + val,
    0
  );
  const remainingBudget = budget - totalExpenses - savings;
  const savingsPercentage = (savings / budget) * 100;

  const isChallengeSuccessful = () => {
    return (
      savings >= 400 &&
      remainingBudget >= 0 &&
      savingsPercentage >= 20 &&
      Object.keys(expenses).some((key) => expenses[key] > 0)
    );
  };

  const calculateScore = () => {
    let score = 0;

    // Pontos por poupança
    if (savings >= 400) score += 40;
    else score += Math.floor((savings / 400) * 40);

    // Pontos por equilíbrio do orçamento
    if (remainingBudget >= 0) score += 30;
    else score += Math.max(0, 30 + remainingBudget / 10);

    // Pontos por cobertura de despesas essenciais
    const essentialExpenses = expenseCategories.filter((cat) => cat.essential);
    const coveredEssentials = essentialExpenses.filter(
      (cat) => expenses[cat.id] > 0
    ).length;
    score += (coveredEssentials / essentialExpenses.length) * 20;

    // Pontos por diversificação
    const usedCategories = Object.keys(expenses).filter(
      (key) => expenses[key] > 0
    ).length;
    score += Math.min(10, usedCategories * 2);

    return Math.max(0, Math.min(100, score));
  };

  const handleExpenseChange = (categoryId: string, value: number) => {
    setExpenses((prev) => ({
      ...prev,
      [categoryId]: Math.max(0, value),
    }));
  };

  const handleNextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    } else {
      setScore(calculateScore());
      setChallengeCompleted(true);
    }
  };

  const getStepTitle = (step: number) => {
    switch (step) {
      case 0:
        return "Understand the Scenario";
      case 1:
        return "Define your Expenses";
      case 2:
        return "Configure your Savings";
      case 3:
        return "Review your Budget";
      default:
        return "";
    }
  };

  const getScoreMessage = (score: number) => {
    if (score >= 90) return { message: "Excellent!", color: "text-green-400" };
    if (score >= 70) return { message: "Very good!", color: "text-blue-400" };
    if (score >= 50)
      return {
        message: "Good work!",
        color: "text-yellow-400",
      };
    return {
      message: "Keep practicing!",
      color: "text-orange-400",
    };
  };

  const getStarsEarned = (score: number) => {
    if (score >= 80) return 3;
    if (score >= 60) return 2;
    if (score >= 40) return 1;
    return 0;
  };

  if (!user) {
    return null;
  }

  if (challengeCompleted) {
    const scoreData = getScoreMessage(score);
    const starsEarned = getStarsEarned(score);

    return (
      <Layout
        title="Desafio Concluído!"
        subtitle="Parabéns por completar o desafio"
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl mx-auto"
        >
          <div className="card text-center mb-6">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-6xl mb-4"
            >
              <Trophy className="w-16 h-16 text-yellow-400 mx-auto" />
            </motion.div>

            <h2 className={`text-2xl font-bold mb-2 ${scoreData.color}`}>
              {scoreData.message}
            </h2>

            <p className="text-gray-400 mb-6">
              Você completou o desafio de orçamento com {score} pontos
            </p>

            {/* Estrelas conquistadas */}
            <div className="flex justify-center space-x-2 mb-6">
              {[1, 2, 3].map((star) => (
                <motion.div
                  key={star}
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{
                    scale: star <= starsEarned ? 1 : 0.5,
                    rotate: 0,
                  }}
                  transition={{
                    duration: 0.3,
                    delay: 0.5 + star * 0.1,
                  }}
                  className={`text-3xl ${
                    star <= starsEarned ? "text-yellow-400" : "text-gray-600"
                  }`}
                >
                  <Star className="w-8 h-8 text-yellow-400" />
                </motion.div>
              ))}
            </div>

            {/* Resumo do orçamento */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-dark-700 rounded-lg p-4">
                <div className="text-2xl font-bold text-white">
                  R$ {savings.toLocaleString("pt-BR")}
                </div>
                <div className="text-sm text-gray-400">Poupança</div>
              </div>
              <div className="bg-dark-700 rounded-lg p-4">
                <div className="text-2xl font-bold text-white">
                  R$ {totalExpenses.toLocaleString("pt-BR")}
                </div>
                <div className="text-sm text-gray-400">Despesas</div>
              </div>
            </div>

            {/* Análise dos resultados */}
            <div className="bg-gradient-to-r from-primary-600/20 to-blue-600/20 border border-primary-500/30 rounded-lg p-4 mb-6">
              <h3 className="text-lg font-semibold text-white mb-3">
                Análise do seu Orçamento
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Meta de poupança (20%):</span>
                  <span
                    className={
                      savings >= 400 ? "text-green-400" : "text-red-400"
                    }
                  >
                    {savings >= 400 ? "Atingida" : "Não atingida"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Orçamento equilibrado:</span>
                  <span
                    className={
                      remainingBudget >= 0 ? "text-green-400" : "text-red-400"
                    }
                  >
                    {remainingBudget >= 0 ? "Sim" : "Déficit"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Despesas essenciais:</span>
                  <span className="text-blue-400">
                    {
                      expenseCategories.filter(
                        (cat) => cat.essential && expenses[cat.id] > 0
                      ).length
                    }
                    /{expenseCategories.filter((cat) => cat.essential).length}
                  </span>
                </div>
              </div>
            </div>

            {/* Recompensas */}
            <div className="bg-gradient-to-r from-primary-600/20 to-blue-600/20 border border-primary-500/30 rounded-lg p-4 mb-6">
              <h3 className="text-lg font-semibold text-white mb-3">
                Recompensas Conquistadas
              </h3>
              <div className="flex justify-center space-x-6">
                <div className="flex items-center space-x-2">
                  <Star className="w-5 h-5 text-yellow-400" />
                  <span className="text-white font-semibold">
                    +{starsEarned * 20}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <DollarSign className="w-5 h-5 text-green-400" />
                  <span className="text-white font-semibold">
                    +{Math.floor(score / 2)}
                  </span>
                </div>
                {isChallengeSuccessful() && (
                  <div className="flex items-center space-x-2">
                    <Trophy className="w-5 h-5 text-yellow-400" />
                    <span className="text-white font-semibold">
                      Orçamentista!
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Botões de ação */}
            <div className="flex space-x-3">
              <button
                onClick={() => navigate("/finance-school")}
                className="flex-1 py-3 bg-dark-700 hover:bg-dark-600 text-white font-medium rounded-lg transition-colors"
              >
                Voltar à Escolinha
              </button>
              <button
                onClick={() => {
                  setCurrentStep(0);
                  setExpenses({});
                  setSavings(0);
                  setChallengeCompleted(false);
                  setScore(0);
                }}
                className="flex-1 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
              >
                Refazer Desafio
              </button>
            </div>
          </div>
        </motion.div>
      </Layout>
    );
  }

  return (
    <Layout
      title={challenge.title}
      subtitle={`Passo ${currentStep + 1} de 4: ${getStepTitle(currentStep)}`}
    >
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="max-w-4xl mx-auto"
      >
        {/* Header do desafio */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate("/finance-school")}
            className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Voltar</span>
          </button>

          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <DollarSign className="w-4 h-4 text-green-400" />
              <span className="text-white font-semibold">
                R$ {budget.toLocaleString("pt-BR")}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <Target className="w-4 h-4 text-primary-400" />
              <span className="text-white font-semibold">
                {Math.round(savingsPercentage)}%
              </span>
            </div>
          </div>
        </div>

        {/* Barra de progresso */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-gray-400 mb-2">
            <span>Progresso do Desafio</span>
            <span>{currentStep + 1}/4</span>
          </div>
          <div className="w-full bg-dark-700 rounded-full h-2">
            <motion.div
              initial={{ width: 0 }}
              animate={{
                width: `${((currentStep + 1) / 4) * 100}%`,
              }}
              transition={{ duration: 0.3 }}
              className="h-2 bg-gradient-to-r from-primary-500 to-blue-500 rounded-full"
            />
          </div>
        </div>

        {/* Conteúdo do passo atual */}
        <AnimatePresence mode="wait">
          {currentStep === 0 && (
            <motion.div
              key="step-0"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.3 }}
              className="card mb-6"
            >
              <h3 className="text-xl font-semibold text-white mb-4">
                Cenário do Desafio
              </h3>
              <p className="text-gray-300 mb-4">{challenge.scenario}</p>

              <div className="bg-blue-600/20 border border-blue-500/30 rounded-lg p-4 mb-4">
                <h4 className="text-blue-400 font-semibold mb-2">
                  Seus Objetivos:
                </h4>
                <ul className="space-y-1">
                  {challenge.goals.map((goal, index) => (
                    <li
                      key={index}
                      className="text-gray-300 text-sm flex items-center space-x-2"
                    >
                      <CheckSquare className="w-4 h-4 text-blue-400" />
                      <span>{goal}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-yellow-600/20 border border-yellow-500/30 rounded-lg p-4">
                <h4 className="text-yellow-400 font-semibold mb-2">Dicas:</h4>
                <ul className="space-y-1">
                  {challenge.hints.slice(0, 2).map((hint, index) => (
                    <li
                      key={index}
                      className="text-gray-300 text-sm flex items-center space-x-2"
                    >
                      <Lightbulb className="w-4 h-4 text-yellow-400" />
                      <span>{hint}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          )}

          {currentStep === 1 && (
            <motion.div
              key="step-1"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.3 }}
              className="card mb-6"
            >
              <h3 className="text-xl font-semibold text-white mb-4">
                Configure suas Despesas
              </h3>
              <p className="text-gray-400 mb-6">
                Defina quanto você gasta em cada categoria. Lembre-se: comece
                pelas despesas essenciais!
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {expenseCategories.map((category) => (
                  <div key={category.id} className="bg-dark-700 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <category.icon className="w-6 h-6 text-primary-400" />
                        <div>
                          <h4 className="text-white font-medium">
                            {category.name}
                          </h4>
                          {category.essential && (
                            <span className="text-xs text-red-400 bg-red-400/20 px-2 py-1 rounded">
                              Essencial
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-sm text-gray-400">
                        Máx: R$ {category.max}
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <span className="text-gray-400">R$</span>
                      <input
                        type="number"
                        min="0"
                        max={category.max}
                        value={expenses[category.id] || 0}
                        onChange={(e) =>
                          handleExpenseChange(
                            category.id,
                            Number(e.target.value)
                          )
                        }
                        className="flex-1 px-3 py-2 bg-dark-800 border border-gray-600 rounded text-white"
                        placeholder="0"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {currentStep === 2 && (
            <motion.div
              key="step-2"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.3 }}
              className="card mb-6"
            >
              <h3 className="text-xl font-semibold text-white mb-4">
                Defina sua Poupança
              </h3>
              <p className="text-gray-400 mb-6">
                Quanto você quer economizar por mês? Lembre-se: a meta é pelo
                menos 20% da sua renda (R$ 400).
              </p>

              <div className="bg-green-600/20 border border-green-500/30 rounded-lg p-6 mb-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-white mb-2">
                    R$ {savings.toLocaleString("pt-BR")}
                  </div>
                  <div className="text-gray-400 mb-4">
                    {savingsPercentage.toFixed(1)}% da sua renda
                  </div>

                  <input
                    type="range"
                    min="0"
                    max={budget - totalExpenses}
                    value={savings}
                    onChange={(e) => setSavings(Number(e.target.value))}
                    className="w-full h-2 bg-dark-700 rounded-lg appearance-none cursor-pointer"
                  />

                  <div className="flex justify-between text-xs text-gray-400 mt-2">
                    <span>R$ 0</span>
                    <span>R$ {budget - totalExpenses}</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-dark-700 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-white">
                    R$ {totalExpenses.toLocaleString("pt-BR")}
                  </div>
                  <div className="text-sm text-gray-400">Total de Despesas</div>
                </div>
                <div className="bg-dark-700 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-white">
                    R$ {remainingBudget.toLocaleString("pt-BR")}
                  </div>
                  <div className="text-sm text-gray-400">
                    Orçamento Restante
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {currentStep === 3 && (
            <motion.div
              key="step-3"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.3 }}
              className="card mb-6"
            >
              <h3 className="text-xl font-semibold text-white mb-4">
                Resumo do seu Orçamento
              </h3>

              {/* Resumo financeiro */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-dark-700 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-white">
                    R$ {budget.toLocaleString("pt-BR")}
                  </div>
                  <div className="text-sm text-gray-400">Renda Total</div>
                </div>
                <div className="bg-dark-700 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-white">
                    R$ {totalExpenses.toLocaleString("pt-BR")}
                  </div>
                  <div className="text-sm text-gray-400">Despesas</div>
                </div>
                <div className="bg-dark-700 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-white">
                    R$ {savings.toLocaleString("pt-BR")}
                  </div>
                  <div className="text-sm text-gray-400">Poupança</div>
                </div>
              </div>

              {/* Análise */}
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-dark-700 rounded-lg">
                  <span className="text-gray-400">Meta de poupança (20%):</span>
                  <span
                    className={
                      savings >= 400 ? "text-green-400" : "text-red-400"
                    }
                  >
                    {savings >= 400 ? "Atingida" : "Não atingida"}
                  </span>
                </div>

                <div className="flex items-center justify-between p-3 bg-dark-700 rounded-lg">
                  <span className="text-gray-400">Orçamento equilibrado:</span>
                  <span
                    className={
                      remainingBudget >= 0 ? "text-green-400" : "text-red-400"
                    }
                  >
                    {remainingBudget >= 0
                      ? "Sim"
                      : "Déficit de R$ " +
                        Math.abs(remainingBudget).toLocaleString("pt-BR")}
                  </span>
                </div>

                <div className="flex items-center justify-between p-3 bg-dark-700 rounded-lg">
                  <span className="text-gray-400">
                    Despesas essenciais cobertas:
                  </span>
                  <span className="text-blue-400">
                    {
                      expenseCategories.filter(
                        (cat) => cat.essential && expenses[cat.id] > 0
                      ).length
                    }
                    /{expenseCategories.filter((cat) => cat.essential).length}
                  </span>
                </div>
              </div>

              {remainingBudget < 0 && (
                <div className="mt-4 p-4 bg-red-600/20 border border-red-500/30 rounded-lg">
                  <div className="flex items-center space-x-2 text-red-400">
                    <AlertCircle className="w-5 h-5" />
                    <span className="font-semibold">Atenção!</span>
                  </div>
                  <p className="text-gray-300 text-sm mt-2">
                    Seu orçamento está em déficit. Considere reduzir algumas
                    despesas ou ajustar sua poupança.
                  </p>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Botões de navegação */}
        <div className="flex justify-between">
          <button
            onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
            disabled={currentStep === 0}
            className="px-6 py-3 bg-dark-700 hover:bg-dark-600 disabled:bg-gray-800 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
          >
            Anterior
          </button>

          <div className="flex space-x-3">
            <button
              onClick={() => setShowHints(!showHints)}
              className="px-4 py-3 bg-yellow-600/20 hover:bg-yellow-600/30 text-yellow-400 font-medium rounded-lg transition-colors flex items-center space-x-2"
            >
              <Lightbulb className="w-4 h-4" />
              <span>Dicas</span>
            </button>

            <button
              onClick={handleNextStep}
              className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors flex items-center space-x-2"
            >
              <span>{currentStep < 3 ? "Próximo" : "Finalizar"}</span>
              {currentStep < 3 ? (
                <ArrowLeft className="w-4 h-4 rotate-180" />
              ) : (
                <CheckCircle className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>

        {/* Dicas flutuantes */}
        <AnimatePresence>
          {showHints && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="fixed bottom-20 left-4 right-4 bg-dark-800 border border-yellow-500/30 rounded-lg p-4 z-50"
            >
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="text-yellow-400 font-semibold mb-2">
                    Dicas para este passo:
                  </h4>
                  <ul className="space-y-1">
                    {challenge.hints
                      .slice(currentStep * 2, (currentStep + 1) * 2)
                      .map((hint, index) => (
                        <li
                          key={index}
                          className="text-gray-300 text-sm flex items-start space-x-2"
                        >
                          <span className="text-yellow-400 mt-1">•</span>
                          <span>{hint}</span>
                        </li>
                      ))}
                  </ul>
                </div>
                <button
                  onClick={() => setShowHints(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </Layout>
  );
};

export default ChallengePage;
